import React, { useContext, useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle, GridDirectedLineChart } from '@mesh.ai/rabbit';
import { ProfileContext } from 'Deer/pages/Profile/hooks/ProfileContext';
import { getGridTopToBottomFlatOrderData } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/cards/Reviews/DirectReportsReviewsOverview';
import { isEmpty } from 'lodash';
import { checkReviewsOverviewBoxGridEnabled } from 'utils/helpers/featureFlags';

import noInsights from 'assets/assetsV1/illustrations/deerIllustrations/noInsights.svg';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { PerformanceReviewGraphV1Wrapper, apiToLineChartDataTransformer } from './PerformanceReviewGraphV1Wrapper';
import { apiToGridDirectedChartDataTransformer } from './apiToGridDirectedChartDataTransformer';
import { useGetGridChartData } from './dataHooks';

const commonArgs = {
  xAxisLabel: 'Performance (Goal score)',
  yAxisLabel: 'Potential (Competency score)',
  connectNulls: true,
  tooltipTitle: 'Average Score',
  withBackground: false,
};

export const ReviewsChart = () => {
  const employeeContext = useContext(ProfileContext);

  const { data, isLoading, isError } = useGetGridChartData({
    cohortToPass: null,
    employeeId: employeeContext?.id ? employeeContext.id : undefined,
  });

  const { gridDefinition = [], employeeReviewCycleData = [] } = data ?? {};
  const is1dVersion = gridDefinition?.length === 1;

  const chartProps = useMemo(() => {
    const transformedData =
      is1dVersion || !checkReviewsOverviewBoxGridEnabled()
        ? apiToLineChartDataTransformer({ nodes: employeeReviewCycleData })
        : apiToGridDirectedChartDataTransformer(employeeReviewCycleData);

    if (is1dVersion || !checkReviewsOverviewBoxGridEnabled()) {
      return transformedData;
    }
    return {
      ...commonArgs,
      nodes: transformedData,
      yAxisDomain: [0, 5],
      xAxisDomain: [0, 5],
    };
  }, [employeeReviewCycleData, is1dVersion]);

  let isEmptyData = employeeReviewCycleData.length === 0 || isEmpty(gridDefinition);
  if (is1dVersion || !checkReviewsOverviewBoxGridEnabled()) {
    isEmptyData = isEmptyData || chartProps?.performanceReviewData?.reviewRatingDetailsResponseList?.length === 0;
  } else {
    isEmptyData = isEmptyData || chartProps?.nodes?.length === 0;
  }

  const gridChartProps = useMemo(() => {
    const boxData = getGridTopToBottomFlatOrderData(gridDefinition);

    return {
      boxData,
      rows: gridDefinition.length,
      columns: gridDefinition?.[0]?.length,
      boxHeight: '90px',
      position: 'absolute',
      margin: '0',
      width: '100%',
      left: 0,
      top: 0,
      isExpanded: false,
      isTextCenterAlign: false,
    };
  }, [gridDefinition]);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (isError || isEmptyData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Assessment</CardTitle>
        </CardHeader>

        <CardContent
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EmptyScreen
            illustrationSrc={noInsights}
            heading="Not enough data to show insights"
            isSmallTextSize
            description="Insights on your progress would unlock as you complete review cycles"
            smallTextMargin="0px"
            illustrationStyle={{
              width: '180px',
            }}
          />
        </CardContent>
      </Card>
    );
  }

  if (is1dVersion || !checkReviewsOverviewBoxGridEnabled()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Assessment</CardTitle>
        </CardHeader>

        <CardContent
          style={{
            height: '326px',
          }}
        >
          <PerformanceReviewGraphV1Wrapper lineChartData={chartProps} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance grid</CardTitle>
      </CardHeader>

      <CardContent
        style={{
          height: '326px',
        }}
      >
        <div style={{ height: '330px' }}>
          <GridDirectedLineChart gridChartProps={gridChartProps} directedLineChartProps={chartProps} mode="mini" />
        </div>
      </CardContent>
    </Card>
  );
};
